"use client";

import type {
  ScheduleSettings,
  UpdateBookingConstraintsRequest,
  UpdateDefaultTemplateRequest,
} from "@/features/calendar/types/schedule-settings.ts";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { useDebouncedCallback } from "use-debounce";

import { toast } from "@/utils/customToast";
import {
  getScheduleSettings,
  resetGoogleCalendar,
  updateBookingConstraints,
  updateDefaultTemplate,
} from "@/features/calendar/api/Settings";
import { TemplateSettingsCard } from "@/features/calendar/components/settings/TemplateSettingsCard.tsx";
import { GoogleCalendarCard } from "@/features/calendar/components/settings/GoogleCalendarCard.tsx";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { TimeSettingsCard } from "@/features/calendar/components/settings/TimeSettingsCard.tsx";
import GenericLoader from "@/components/GenericLoader";
import { CoachMarker } from "@/components/CoachMarker";
import envs from "@/envs.ts";

export const ScheduleSettingsPage = () => {
  const { t } = useTranslation(["calendar", "common", "help"]);
  const [settings, setSettings] = useState<ScheduleSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [savingSection, setSavingSection] = useState<"template" | "time" | "google" | null>(null);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await getScheduleSettings();
      const data = response.data.data;

      setSettings({ ...data });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error(t("calendar:settings.errors.fetchFailed"));
    } finally {
      setIsLoading(false);
    }
  };
  const debouncedFetchSettings = useDebouncedCallback(fetchSettings, 500);

  useEffect(() => {
    debouncedFetchSettings();
  }, []);

  const handleSaveTemplateSettings = async (data: UpdateDefaultTemplateRequest) => {
    if (!settings) return;

    setIsSaving(true);
    setSavingSection("template");
    try {
      const response = await updateDefaultTemplate(data);

      setSettings(response.data.data);
      toast.success(t("calendar:settings.success.saved"));
    } catch (error) {
      console.error("Failed to update template settings:", error);
      toast.error(t("calendar:settings.errors.saveFailed"));
    } finally {
      setIsSaving(false);
      setSavingSection(null);
    }
  };

  const handleSaveTimeSettings = async (data: UpdateBookingConstraintsRequest) => {
    if (!settings) return;

    setIsSaving(true);
    setSavingSection("time");
    try {
      const response = await updateBookingConstraints(data);

      setSettings(response.data.data);
      toast.success(t("calendar:settings.success.saved"));
    } catch (error) {
      console.error("Failed to update time settings:", error);
      toast.error(t("calendar:settings.errors.saveFailed"));
    } finally {
      setIsSaving(false);
      setSavingSection(null);
    }
  };

  const handleConnectGoogleCalendar = () => {
    window.location.href = `${envs.VITE_API_URL}/authentication/oauth/google`;
  };

  const handleDisconnectGoogleCalendar = async () => {
    if (!settings) return;

    setIsSaving(true);
    setSavingSection("google");
    try {
      await resetGoogleCalendar();

      // Refresh settings after disconnection
      await fetchSettings();

      toast.success(t("calendar:settings.googleCalendar.disconnectSuccess"));
    } catch (error) {
      console.error("Failed to disconnect Google Calendar:", error);
      toast.error(t("calendar:settings.googleCalendar.resetError"));
    } finally {
      setIsSaving(false);
      setSavingSection(null);
    }
  };

  const handleResetGoogleCalendar = async () => {
    if (!settings) return;

    setIsResetting(true);
    try {
      await resetGoogleCalendar();

      // Refresh settings after reset
      await fetchSettings();

      toast.success(t("calendar:settings.googleCalendar.resetSuccess"));
    } catch (error) {
      console.error("Failed to reset Google Calendar:", error);
      toast.error(t("calendar:settings.googleCalendar.resetError"));
    } finally {
      setIsResetting(false);
    }
  };

  const handleReauthGoogleCalendar = () => {
    // Redirect to Google account management to reauthenticate
    window.open("https://myaccount.google.com/permissions", "_blank");
  };

  if (isLoading) {
    return <GenericLoader />;
  }

  if (!settings) {
    return (
      <div className="flex h-[calc(100dvh_-_4rem)] items-center justify-center">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
        >
          <Icon className="text-destructive mx-auto h-10 w-10" icon="mdi:alert-circle" />
          <p className="text-accent-foreground mt-4 text-base font-medium">{t("common:error")}</p>
          <p className="text-muted-foreground mt-1 text-sm">
            {t("calendar:settings.errors.noSettings")}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh_-_4rem)]">
      <title>{`${t("calendar:settings.title")} - Ximly`}</title>
      <ScrollArea className="h-full">
        <div className="container mx-auto max-w-4xl px-6 py-8">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-accent-foreground text-2xl font-bold">
              {t("calendar:settings.title")}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">{t("calendar:settings.subtitle")}</p>
          </motion.div>

          <div className="space-y-8">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="text-muted-foreground mb-4 text-xs tracking-wider uppercase">
                {t("common:integrations")}
              </div>
              <CoachMarker
                description={t("help:coachMarkers.googleCalendar.description", {
                  defaultValue:
                    "Connect your Google Calendar to automatically sync your availability and prevent double bookings. This helps students see your real-time availability and ensures you don't get booked when you're busy.",
                })}
                id="google-calendar-integration"
                placement="left"
                title={t("help:coachMarkers.googleCalendar.title", {
                  defaultValue: "Google Calendar Integration",
                })}
              >
                <GoogleCalendarCard
                  isLoading={isSaving && savingSection === "google"}
                  isResetting={isResetting}
                  status={settings.google_calendar}
                  onConnect={handleConnectGoogleCalendar}
                  onDisconnect={handleDisconnectGoogleCalendar}
                  onReauth={handleReauthGoogleCalendar}
                  onReset={handleResetGoogleCalendar}
                />
              </CoachMarker>
            </motion.div>

            <Separator className="bg-border/30" />

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="text-muted-foreground mb-4 text-xs tracking-wider uppercase">
                {t("calendar:templates.title")}
              </div>
              <CoachMarker
                description={t("help:coachMarkers.templateSettings.description", {
                  defaultValue:
                    "Set a default template to automatically fill new weeks with your standard lesson slots. This saves time when creating your schedule and ensures consistency across your calendar.",
                })}
                id="default-template-settings"
                placement="left"
                title={t("help:coachMarkers.templateSettings.title", {
                  defaultValue: "Default Template Settings",
                })}
              >
                <TemplateSettingsCard
                  defaultTemplateId={settings.default_template_id}
                  disabled={isSaving}
                  isSaving={isSaving && savingSection === "template"}
                  onSave={handleSaveTemplateSettings}
                />
              </CoachMarker>
            </motion.div>

            <Separator className="bg-border/30" />

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="text-muted-foreground mb-4 text-xs tracking-wider uppercase">
                {t("common:timeConstraints")}
              </div>
              <CoachMarker
                description={t("help:coachMarkers.timeSettings.description", {
                  defaultValue:
                    "Set how far in advance students can book lessons and how much notice they need to give for cancellations. This helps you manage your schedule and protects you from last-minute changes.",
                })}
                id="time-constraints"
                placement="left"
                title={t("help:coachMarkers.timeSettings.title", {
                  defaultValue: "Time Constraints",
                })}
              >
                <TimeSettingsCard
                  bookingTime={settings.min_time_before_booking_lesson}
                  cancellationTime={settings.min_time_before_cancelling_lesson_without_provision}
                  disabled={isSaving}
                  isSaving={isSaving && savingSection === "time"}
                  onSave={handleSaveTimeSettings}
                />
              </CoachMarker>
            </motion.div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
